import React from "react";
import { BiSearch, BiMenu, BiX } from "react-icons/bi";
import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getCurrentUser } from "../utils/auth";
import api from "../utils/api";
import { FaUser } from "react-icons/fa";

function Header() {
  const [profile, setProfile] = useState(null);
  const [profileError, setProfileError] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const profileRef = useRef(null);
  const logoutPending = useRef(false);
  useEffect(() => {
    const syncLogin = () => {
      const loggedIn = localStorage.getItem("isLoggedIn") === "true" && Boolean(localStorage.getItem("accessToken"));
      setIsLoggedIn(loggedIn);
      if (!loggedIn) setShowProfileMenu(false);
    };
    syncLogin();
    window.addEventListener("auth-state-changed", syncLogin);
    window.addEventListener("storage", syncLogin);
    return () => {
      window.removeEventListener("auth-state-changed", syncLogin);
      window.removeEventListener("storage", syncLogin);
    };
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    let mounted = true;
    if (!isLoggedIn) { setProfile(null); return; }
    setProfileError(false);
    getCurrentUser().then(result => {
      if (!mounted) return;
      setProfile(result.success ? result.user : null);
      setProfileError(!result.success);
    });
    return () => { mounted = false; };
  }, [isLoggedIn, showProfileMenu, location.pathname]);

  const isActive = (path) => location.pathname === path;

  const getNavLinkClass = (path) =>
    `cursor-pointer font-semibold text-[18px] transition-colors ${
      isActive(path) ? "text-yellow-400" : "text-white hover:text-yellow-400"
    }`;

  const handleLogout = async () => {
    if (logoutPending.current) return;
    logoutPending.current = true;
    window.dispatchEvent(new Event("game-session-signout-start"));
    try {
      const { data } = await api.post("/auth/game-session/release", {}, { timeout: 8000 });
      if (!data.success) throw new Error("Unable to sign out");
      ["accessToken", "user", "subscription", "isLoggedIn"].forEach(key => localStorage.removeItem(key));
      window.dispatchEvent(new Event("auth-state-changed"));
      setIsLoggedIn(false);
      setShowProfileMenu(false);
      navigate("/games");
    } catch {
      window.dispatchEvent(new Event("game-session-signout-cancelled"));
      window.alert("Unable to sign out. Please try again.");
    } finally {
      logoutPending.current = false;
    }
  };

  const navAndClose = (path) => {
    setMenuOpen(false);
    setShowSearch(false);
    navigate(path);
  };

  return (
    <header
      className={`w-full z-[9999] transition-all duration-300 ${
        scrolled ? "fixed top-0 left-0 bg-[#0b0b1d] shadow-lg" : "relative bg-[#0b0b1d]"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 py-4 relative bg-[#0b0b1d]">
        <div className="flex items-center justify-between bg-[#0b0b1d]">
          <div
            onClick={() => navAndClose("/")}
            className="cursor-pointer"
            aria-label="Go home"
          >
            <img src="/assets/logo.webp" className="h-10" alt="logo" />
          </div>

          <ul className="hidden md:flex items-center space-x-9">
            <li onClick={() => navAndClose("/")} className={getNavLinkClass("/")}>Home</li>
            {!isLoggedIn ? (
              <li onClick={() => navAndClose("/games")} className={getNavLinkClass("/games")}>Games</li>
            ) : (
              <li onClick={() => navAndClose("/play")} className={getNavLinkClass("/play")}>Play</li>
            )}
            <li onClick={() => navAndClose("/news")} className={getNavLinkClass("/news")}>News</li>
            <li onClick={() => navAndClose("/support")} className={getNavLinkClass("/support")}>Support</li>
          </ul>

          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-2">
              <div
                className={`flex items-center gap-2 transition-all duration-300 ${
                  showSearch ? "w-72 opacity-100" : "w-0 opacity-0 overflow-hidden"
                }`}
              >
                <input
                  className="w-full p-2 rounded-xl bg-[#3c3c54] text-white outline-none"
                  type="search"
                  placeholder="Enter keyword"
                  aria-label="Search"
                />
                <button className="px-4 py-2 rounded-xl bg-yellow-400 text-black font-semibold">Search</button>
              </div>

              <button
                onClick={() => setShowSearch(!showSearch)}
                className="text-white hover:text-yellow-400"
                aria-label="Toggle search"
              >
                {showSearch ? <BiX size={24} /> : <BiSearch size={24} />}
              </button>
            </div>

            {isLoggedIn && (
              <div className="relative" ref={profileRef}>
                <div
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="w-10 h-10 rounded-full overflow-hidden border-2 border-yellow-400 cursor-pointer"
                  aria-label="Toggle profile menu"
                >
                  <img src="/assets/game-banner-illus-4.webp" alt="profile" className="w-full h-full object-cover" />
                </div>

                {showProfileMenu && (
                  <div className="absolute right-0 mt-3 w-80 bg-[#3b3b54] rounded-2xl shadow-2xl overflow-hidden z-[9999]">
                    <div className="text-center py-4">
                      <h2 className="text-xl font-bold text-white">Hello</h2>
                      <p className="text-gray-300 text-sm">{profile?.msisdn ? `+${profile.msisdn}` : profileError ? "Unable to load profile" : "Loading..."}</p>
                    </div>

                    <div className="bg-[#50506d] rounded-xl p-4 flex gap-4 m-4">
                      <div>
                        <p className="text-green-400 font-bold">{profile ? `₦${Number(profile.balance ?? 0).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}</p>
                        <p className="text-gray-300 text-sm">Balance</p>
                      </div>
                    </div>

                    <div className="bg-[#50506d] rounded-xl p-4 flex justify-between m-4">
                      <div>
                        <p className="text-green-400 font-bold">{profile ? Number(profile.points ?? 0).toLocaleString("en-NG") : "—"}</p>
                        <p className="text-gray-300 text-sm">Points</p>
                      </div>
                      <button className="bg-[#6b6b8f] text-white px-3 py-1 rounded-lg text-sm">Exchange</button>
                    </div>

                    <div
                      onClick={() => {
                        setShowProfileMenu(false);
                        navAndClose("/myaccount");
                      }}
                      className="flex justify-between items-center px-6 py-4 cursor-pointer"
                    >
                      <span className="flex items-center gap-2 text-white font-semibold text-[18px]">
                        <FaUser /> My Account
                      </span>
                      <span className="text-yellow-400">&gt;</span>
                    </div>

                    <button onClick={handleLogout} className="w-full py-4 border-t border-gray-600 text-white font-semibold">
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            )}

            <button
              className="md:hidden text-yellow-400"
              onClick={() => {
                setMenuOpen(!menuOpen);
                setShowSearch(false);
              }}
              aria-label="Toggle menu"
            >
              {menuOpen ? <BiX size={28} /> : <BiMenu size={28} />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="md:hidden mt-3 w-full">
            <div className="w-full bg-transparent text-white">
              <ul className="flex flex-col gap-4 px-2 py-2">
                <li onClick={() => navAndClose("/")} className={`pl-3 ${isActive("/") ? "text-yellow-400 font-bold" : "text-white"} cursor-pointer text-[18px]`}>Home</li>
                {!isLoggedIn ? (
                  <li onClick={() => navAndClose("/games")} className={`pl-3 ${isActive("/games") ? "text-yellow-400 font-bold" : "text-white"} cursor-pointer text-[18px]`}>Games</li>
                ) : (
                  <li onClick={() => navAndClose("/play")} className={`pl-3 ${isActive("/play") ? "text-yellow-400 font-bold" : "text-white"} cursor-pointer text-[18px]`}>Play</li>
                )}
                <li onClick={() => navAndClose("/news")} className={`pl-3 ${isActive("/news") ? "text-yellow-400 font-bold" : "text-white"} cursor-pointer text-[18px]`}>News</li>
                <li onClick={() => navAndClose("/support")} className={`pl-3 ${isActive("/support") ? "text-yellow-400 font-bold" : "text-white"} cursor-pointer text-[18px]`}>Support</li>

                <li className="pl-3 pt-2">
                  <div className="flex flex-wrap items-center gap-3">
                    <button onClick={() => setShowSearch(!showSearch)} className="text-white" aria-label="Toggle mobile search">
                      {showSearch ? <BiX size={20} /> : <BiSearch size={20} />}
                    </button>
                  </div>

                  {showSearch && (
                    <div className="mt-2 flex gap-3">
                      <input
                        className="flex-1 p-2 rounded-xl bg-[#2a2a3a] text-white outline-none"
                        type="search"
                        placeholder="Enter Keywords......"
                      />
                      <button className="bg-yellow-400 px-4 py-2 rounded-xl font-semibold">Search</button>
                    </div>
                  )}
                </li>
              </ul>
            </div>
          </div>
        )}
      </nav>

      <style>{`
        header {
          position: sticky;
          top: 0;
        }
      `}</style>
    </header>
  );
}

export default Header;
